<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed, inject } from "vue"
import { useI18n } from "vue-i18n"
import { useTaskStore } from "@core/stores/task.store"
import { useFetchWrapper } from "@core/composables"
import { formatDate } from "@core/helpers"
import type { Services } from "@services"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            component: {
                dashboard_iot_header: {
                    last_update: "Dernière mise à jour :",
                    download_report: "Télécharger le rapport",
                    excel: "Génération du rapport Excel",
                },
            },
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        updatedAt?: string | null
        lastUpdateTitle?: string
        downloadable?: boolean
        urlExcel?: string | null
        dateExpirationRapport?: string | null
        analyseId?: string | null
    }>(),
    {
        updatedAt: null,
        lastUpdateTitle: "",
        downloadable: false,
        urlExcel: null,
        dateExpirationRapport: null,
        analyseId: null,
    }
)

const emit = defineEmits<{
    "update:refresh": []
}>()

// ================================
// COMPOSABLES
// ================================
const taskStore = useTaskStore()
const services = inject<Services>("services")

// ================================
// DONNÉES CALCULÉES
// ================================
const isReportAvailable = computed(() => {
    return (
        !!props.urlExcel &&
        !!props.dateExpirationRapport &&
        new Date(props.dateExpirationRapport) > new Date()
    )
})

const showDownloadButton = computed(() => {
    return props.downloadable
})

// ================================
// LOGIQUE MÉTIER
// ================================
const { fetch: fetchGenerateReport, isFetching: isFetchingReport } =
    useFetchWrapper(async (controller?: AbortController) => {
        if (!props.analyseId) throw new Error("analyseId manquant")

        if (!services?.$generateDashboardAnalyseReport) {
            throw new Error("Service de génération de rapport non disponible")
        }

        const { task_id, event_name } =
            await services.$generateDashboardAnalyseReport({
                analyseId: props.analyseId,
                controller,
            })

        taskStore.followEvent(task_id, event_name, {
            taskname: t("component.dashboard_iot_header.excel"),
            notification: true,
            autoDelete: true,
            onSuccess: (data: any) => {
                emit("update:refresh")

                // Si on a un data.result.url_excel alors on l'ouvre dans un nouvel onglet
                if (data?.data?.result?.url_excel) {
                    window.open(data?.data?.result?.url_excel, "_blank")
                }
            },
            onError: (error) => {
                console.error("Erreur lors de la génération du rapport:", error)
            },
        })
    })

// ================================
// INTERACTIONS
// ================================
async function onClickDownloadReport(): Promise<void> {
    try {
        if (isReportAvailable.value && props.urlExcel) {
            window.location.assign(props.urlExcel)
        } else {
            await fetchGenerateReport()
        }
    } catch (err) {
        console.error("Erreur lors du téléchargement du rapport:", err)
    }
}
</script>

<template>
    <div class="flex flex-col gap-2 justify-center items-center">
        <p
            v-if="updatedAt"
            class="flex items-center flex-wrap gap-4 text-sm text-primary-700"
        >
            {{
                lastUpdateTitle ||
                t("component.dashboard_iot_header.last_update")
            }}
            <span class="font-semibold">{{ formatDate(updatedAt) }}</span>
        </p>

        <BaseButton
            v-if="showDownloadButton"
            :label="t('component.dashboard_iot_header.download_report')"
            icon="pi pi-download"
            class="w-fit"
            size="small"
            :loading="isFetchingReport"
            @click="onClickDownloadReport"
        />
    </div>
</template>
